import { HpSvgIconTypes, SvgIcon } from '@avantia/style-guide';
import { Link, useHistory } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { ExternalLink } from 'components/shared/externalLink';

import { useGetScreenSize, getDateWithoutTime } from 'utils';
import { PATH } from 'interfaces/pathTypes';
import { ArrowLink } from 'components/shared/arrowLink';
import { formatDate } from 'utils';
import { PolicyField } from '../../../../shared/policyField';

import { addDays } from 'date-fns';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { getStatusDetails } from './policyStatusConfig';
import { PolicyLifecycleStatus } from 'interfaces/customerDataTypes';

import './policyStatus.scss';
interface PolicyStatusProps {
  policyStatus: PolicyLifecycleStatus;
  stopRenewal: boolean;
  policyRef: string;
  endDate: string;
  daysUntilRenewal: number;
}

export interface ActionButtonProps {
  text: string;
  to: string;
  id?: string;
  isExternal?: boolean;
  onClick?: () => void;
}

export const createActionButton = ({
  text,
  to,
  id = 'policy-button',
  isExternal = false,
  onClick
}: ActionButtonProps): JSX.Element => {
  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const buttonContent = (
    <Button id={id} onClick={onClick ? handleClick : undefined}>
      <p className="av-text-large policy-button-text">{text}</p>
    </Button>
  );

  if (onClick) {
    return buttonContent;
  }

  if (isExternal) {
    return (
      <ExternalLink href={to} id={`${id}-external`}>
        {buttonContent}
      </ExternalLink>
    );
  }

  return <Link to={to}>{buttonContent}</Link>;
};

export const PolicyStatus = ({
  policyStatus,
  daysUntilRenewal,
  endDate,
  stopRenewal,
  policyRef
}: PolicyStatusProps): JSX.Element => {
  const { isMobile } = useGetScreenSize();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { isAgent, email, authType } = useAppSelector((state) => state.authentication);

  const autoRenewalPreference = !stopRenewal ? (
    <p>Automatically renew this policy</p>
  ) : (
    <div className="auto-renewal-message">
      <p>Cover will end on {formatDate(endDate)}</p>
      <SvgIcon type={'pen' as HpSvgIconTypes} size="very-tiny" />
      <Link to={`${PATH.SETTINGS}/policy/${policyRef}`}>Edit</Link>
    </div>
  );
  const {
    iconUrl,
    getActionButtonConfig,
    getHelperText,
    showRenewalDate,
    showAutoRenewalPreference,
    className,
    documentsLinkText,
    statusText
  } = getStatusDetails(policyStatus, stopRenewal, email);

  const formattedRenewalDate = formatDate(addDays(getDateWithoutTime(endDate), 1));
  const actionButtonConfig =
    getActionButtonConfig && getActionButtonConfig({ isAgent, policyRef, dispatch, history, authType });
  const helperText = getHelperText && getHelperText({ policyRef, daysUntilRenewal });

  return (
    <>
      <div className="policy-status">
        <img alt="Policy status icon" src={iconUrl} height={isMobile ? 40 : 64} width={isMobile ? 40 : 64}></img>
        <p className={className} id="policy-status">
          {statusText}
        </p>
      </div>
      {helperText && helperText}
      {actionButtonConfig && (
        <div className="policy-status-buttons-container">
          {actionButtonConfig.map((link, index) => (
            <div key={index} className="policy-status-link">
              {createActionButton(link)}
            </div>
          ))}
        </div>
      )}
      {showRenewalDate && <PolicyField label="Renewal date" value={<p>{formattedRenewalDate}</p>} />}
      {documentsLinkText && (
        <Link to={`${PATH.DOCUMENTS}/policy/${policyRef}`} className="documents-link">
          <ArrowLink linkText={documentsLinkText} />
        </Link>
      )}
      {showAutoRenewalPreference && <PolicyField label="Auto-renewal preference" value={autoRenewalPreference} />}
    </>
  );
};
